import { Box, Button, Stack, styled, Typography } from '@chronosphereio/chrono-ui';
import { memo, useMemo } from 'react';
import { formatValue, TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT, TooltipMode, UnitOptions } from '@/core';
import { TooltipStaticContentWrapper } from '../../PanelTooltip';
import { TooltipSeriesArray } from '../tooltip-model';

export interface TimeSeriesTooltipFooterProps {
  tooltipSeries: TooltipSeriesArray;
  totalSeries: number;
  showSeriesPaginationInfo?: boolean;
  showTotal?: boolean;
  /**
   * Precomputed total value of all series at closestTimestamp.
   * Used when visual.stack option is set.
   */
  total?: number;
  unit?: UnitOptions;
  mode?: TooltipMode;
  maxNearbySeries?: number;
  isStacked?: boolean;
  isShowingAll?: boolean;
  onToggleTooltipMode?: () => void;
}

export const TimeSeriesTooltipFooter = memo(function TimeSeriesTooltipFooter({
  tooltipSeries,
  totalSeries,
  showSeriesPaginationInfo = true,
  showTotal = false,
  total,
  unit,
  mode,
  maxNearbySeries,
  isStacked,
  isShowingAll,
  onToggleTooltipMode,
}: TimeSeriesTooltipFooterProps) {
  // Show tooltip mode only when not stacked and toggle would change the visible series number.
  let canToggleTooltipMode = !isStacked && totalSeries > 1;
  // Account for nearby mode when all series are already shown.
  if (mode === 'nearby' && maxNearbySeries !== undefined && totalSeries <= maxNearbySeries) {
    canToggleTooltipMode = false;
  }

  const toggleTooltipModeText = useMemo(() => {
    if (!isShowingAll && totalSeries >= TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT) {
      return 'Show more';
    }
    if (isShowingAll) {
      return mode === 'single' ? 'Show single' : 'Show less';
    }
    return 'Show all';
  }, [isShowingAll, totalSeries, mode]);

  // Conditional since we only want to included the total when it's precomputed.
  const totalInfo =
    showTotal && total !== undefined ? (
      <TotalStack direction="row" spacing={1} alignItems="center">
        <TotalLabel variant="body2">Total</TotalLabel>
        <Typography variant="body2">{formatValue(total, unit)}</Typography>
      </TotalStack>
    ) : null;

  return (
    <TooltipStaticContentWrapper>
      {totalInfo}
      {showSeriesPaginationInfo && (
        <FooterRow>
          {canToggleTooltipMode && (
            <ModeButton size="small" variant="tertiary" onClick={onToggleTooltipMode}>
              {toggleTooltipModeText}
            </ModeButton>
          )}
          <PaginationWrapper>
            <Typography variant="caption">
              {tooltipSeries.length} of {totalSeries} series
            </Typography>
          </PaginationWrapper>
        </FooterRow>
      )}
    </TooltipStaticContentWrapper>
  );
});

const TotalStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1, 0, 0.5, 0),
  margin: 0,
}));

const TotalLabel = styled(Typography)({
  flexGrow: 1,
  fontWeight: 'bold',
});

const FooterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(0.5),
}));

const PaginationWrapper = styled(Box)({
  flexGrow: 1,
  textAlign: 'right',
});

const ModeButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
}));
